import { create } from 'zustand';

export type MdmDeviceViewMode = 'list' | 'grid';

/**
 * Column config for MDM device table.
 * - `visible`: IDs of columns the user wants to see, in any order.
 * - `order`: canonical column order. Cannot be reordered in Phase 1 but the
 *   field is reserved so future drag-reorder doesn't break persisted state.
 *
 * Pinned columns (`status`, `deviceName`) are ALWAYS rendered regardless of
 * what's stored here — they cannot be hidden via the customizer (spec §5).
 *
 * Staff copy: VPN IP column IS exposed here (staff-only data per spec §9.3).
 * apps/web's copy of this file omits `vpnIp` from both `visible` and `order`.
 */
export interface MdmDeviceColumnConfig {
  visible: string[];
  order: string[];
}

const DEFAULT_COLUMNS: MdmDeviceColumnConfig = {
  visible: [
    'status',
    'deviceName',
    'os',
    'lastHeartBeat',
    'model',
    'localAppVersion',
    // Staff delta: VPN IP default-visible (staff-only column).
    'vpnIp',
  ],
  order: [
    'status',
    'deviceName',
    'fleet',
    'os',
    'lastHeartBeat',
    'model',
    'platform',
    'localAppVersion',
    'vpnIp',
    'serialNo',
    'UUID',
    'tags',
    'tz',
    'activationDate',
  ],
};

/**
 * Columns that were once valid but have been removed from the schema. On load,
 * strip them from persisted configs so stale localStorage doesn't keep asking
 * the table to render a column it no longer knows how to.
 */
const RETIRED_COLUMNS = new Set(['osVersion']);

function sanitizeColumns(col: string[] | undefined, fallback: string[]): string[] {
  if (!Array.isArray(col)) return fallback;
  return col.filter((id) => !RETIRED_COLUMNS.has(id));
}

const VIEW_KEY = 'scio3-staff-mdm-devices-view-mode';
const COLUMN_KEY = 'scio3-staff-mdm-devices-columns';

function loadViewMode(): MdmDeviceViewMode {
  try {
    const saved = localStorage.getItem(VIEW_KEY);
    return saved === 'grid' ? 'grid' : 'list';
  } catch {
    return 'list';
  }
}

function loadColumns(): MdmDeviceColumnConfig {
  try {
    const saved = localStorage.getItem(COLUMN_KEY);
    if (!saved) return DEFAULT_COLUMNS;
    const parsed = JSON.parse(saved) as Partial<MdmDeviceColumnConfig>;
    return {
      visible: sanitizeColumns(parsed.visible, DEFAULT_COLUMNS.visible),
      order: sanitizeColumns(parsed.order, DEFAULT_COLUMNS.order),
    };
  } catch {
    return DEFAULT_COLUMNS;
  }
}

interface MdmDevicesViewState {
  viewMode: MdmDeviceViewMode;
  columnConfig: MdmDeviceColumnConfig;
  setViewMode: (mode: MdmDeviceViewMode) => void;
  setColumnVisibility: (id: string, visible: boolean) => void;
  setColumnConfig: (config: MdmDeviceColumnConfig) => void;
  resetColumns: () => void;
}

export const useMdmDevicesViewStore = create<MdmDevicesViewState>((set, get) => ({
  viewMode: loadViewMode(),
  columnConfig: loadColumns(),

  setViewMode: (viewMode) => {
    try {
      localStorage.setItem(VIEW_KEY, viewMode);
    } catch {
      // ignore storage errors (private mode / quota)
    }
    set({ viewMode });
  },

  setColumnVisibility: (id, visible) => {
    const current = get().columnConfig;
    let nextVisible = current.visible.slice();
    if (visible) {
      if (!nextVisible.includes(id)) nextVisible.push(id);
    } else {
      nextVisible = nextVisible.filter((col) => col !== id);
    }
    const next: MdmDeviceColumnConfig = { ...current, visible: nextVisible };
    try {
      localStorage.setItem(COLUMN_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
    set({ columnConfig: next });
  },

  setColumnConfig: (config) => {
    try {
      localStorage.setItem(COLUMN_KEY, JSON.stringify(config));
    } catch {
      // ignore
    }
    set({ columnConfig: config });
  },

  resetColumns: () => {
    try {
      localStorage.setItem(COLUMN_KEY, JSON.stringify(DEFAULT_COLUMNS));
    } catch {
      // ignore
    }
    set({ columnConfig: DEFAULT_COLUMNS });
  },
}));

export const MDM_DEVICE_DEFAULT_COLUMNS = DEFAULT_COLUMNS;
